import {
    add,
    changeDifficulty,
    changeGameMode,
    changeVariable,
    changeWeather,
    chat,
    checkBlockType,
    connectAndBuild,
    frontSearchBlock,
    frontSetBlock,
    frontSetBlocks,
    frontSpawnEntity,
    frontSpawnParticle,
    frontVariableZ,
    getPosX,
    getPosY,
    getPosZ,
    giveEnchant,
    greenFlagScript,
    ifElse,
    join,
    joinAll,
    numberInput,
    relativeFromVariable,
    repeat,
    resetAroundHere,
    say,
    setBlock,
    setVariable,
    spawnEntity,
    spawnParticle,
    teleport,
    turtleMove,
    turtleTurn,
    variableReporter,
    wait
} from './example-helpers';

const localVariable = name => ({
    name,
    scope: 'local',
    type: ''
});

const basicExamples = [
    {
        id: 'basic-chat-hello',
        level: 'basic',
        title: 'Hola Minecraft',
        description: 'Manda un mensaje al chat del mundo.',
        scripts: [
            greenFlagScript(56, 64, [
                chat('Hola Minecraft')
            ])
        ]
    },
    {
        id: 'basic-place-stone',
        level: 'basic',
        title: 'Piedra delante de ti',
        description: 'Coloca una piedra en el suelo, justo delante del jugador.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSetBlock('Stone', '~', '~-1', '~')
            ]))
        ]
    },
    {
        id: 'basic-glass-cube',
        level: 'basic',
        title: 'Mini cubo de cristal',
        description: 'Construye un cubo de cristal delante de ti, a la altura de los ojos.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSetBlocks('Glass', '~-1', '~', '~-1', '~1', '~2', '~1')
            ]))
        ]
    },
    {
        id: 'basic-reset-zone',
        level: 'basic',
        title: 'Limpieza rapida',
        description: 'Borra una zona cercana para dejar espacio delante y seguir probando.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                chat('Preparo una zona limpia'),
                resetAroundHere(6)
            ]))
        ]
    },
    {
        id: 'basic-teleport-up',
        level: 'basic',
        title: 'Salto sorpresa',
        description: 'Teletransporta al jugador unos bloques hacia arriba.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                teleport('~', '~5', '~'),
                chat('Arriba')
            ]))
        ]
    },
    {
        id: 'basic-show-position',
        level: 'basic',
        title: 'Posicion en pantalla',
        description: 'Lee la posicion del jugador y la muestra con una burbuja de Scratch.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                say(joinAll('X: ', getPosX(), '  Y: ', getPosY(), '  Z: ', getPosZ()))
            ]))
        ]
    },
    {
        id: 'basic-rain-particles',
        level: 'basic',
        title: 'Lluvia brillante',
        description: 'Cambia el clima y crea una columna de fuego delante del jugador.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                changeWeather(1),
                frontSpawnParticle('Flame', '~', '~2', '~', '~', '~4', '~', 0.2, 20)
            ]))
        ]
    },
    {
        id: 'basic-spawn-cow',
        level: 'basic',
        title: 'Invita una vaca',
        description: 'Haz aparecer una vaca delante del jugador.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSpawnEntity('Cow', '~', '~', '~'),
                chat('Llego una vaca')
            ]))
        ]
    },
    {
        id: 'basic-light-column',
        level: 'basic',
        title: 'Columna luminosa',
        description: 'Levanta una columna de glowstone delante de ti.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSetBlocks('Glowstone', '~', '~', '~', '~', '~5', '~'),
                frontSpawnParticle('Heart', '~', '~5', '~', '~', '~6', '~', 0.1, 8)
            ]))
        ]
    },
    {
        id: 'basic-turtle-square',
        level: 'basic',
        title: 'Cuadrado tortuga',
        description: 'La tortuga dibuja un cuadrado delante de ti con bloques.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                repeat(4, [
                    turtleMove('forward', 5),
                    turtleTurn('yaw', 90)
                ])
            ]))
        ]
    }
];

const mediumExamples = [
    {
        id: 'medium-stairs',
        level: 'medium',
        title: 'Escalera automatica',
        description: 'Construye una escalera que sube hacia delante.',
        variables: [localVariable('paso')],
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                setVariable('paso', 0),
                repeat(6, [
                    setBlock('Cobblestone', '~', relativeFromVariable('paso'), frontVariableZ('paso')),
                    changeVariable('paso', 1)
                ])
            ]))
        ]
    },
    {
        id: 'medium-light-bridge',
        level: 'medium',
        title: 'Pasarela brillante',
        description: 'Construye un camino de sea lanterns hacia delante.',
        variables: [localVariable('paso')],
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                setVariable('paso', 0),
                repeat(8, [
                    setBlock('Sea Lantern', '~', '~-1', frontVariableZ('paso')),
                    changeVariable('paso', 1)
                ])
            ]))
        ]
    },
    {
        id: 'medium-glass-lookout',
        level: 'medium',
        title: 'Mirador de cristal',
        description: 'Crea una caja de cristal delante y sube al jugador a mirar.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSetBlocks('Glass', '~-2', '~', '~-2', '~2', '~4', '~2'),
                teleport('~', '~3', '~4'),
                chat('Mirador listo')
            ]))
        ]
    },
    {
        id: 'medium-grass-detector',
        level: 'medium',
        title: 'Detector de hierba',
        description: 'Inspecciona el suelo delante de ti y responde en el chat.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSearchBlock('~', '~-1', '~'),
                ifElse(
                    checkBlockType('Grass'),
                    [chat('Hay hierba delante')],
                    [chat('No es hierba')]
                )
            ]))
        ]
    },
    {
        id: 'medium-four-animals',
        level: 'medium',
        title: 'Cuatro animales',
        description: 'Hace aparecer varios animales en fila delante del jugador.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSpawnEntity('Cow', '~-2', '~', '~'),
                frontSpawnEntity('Sheep', '~2', '~', '~'),
                frontSpawnEntity('Pig', '~', '~', '~2'),
                frontSpawnEntity('Villager', '~', '~', '~-1')
            ]))
        ]
    },
    {
        id: 'medium-storm-scene',
        level: 'medium',
        title: 'Tormenta dramatica',
        description: 'Activa truenos y humo delante del jugador.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                changeDifficulty(3),
                changeWeather(2),
                frontSpawnParticle('Large Smoke', '~', '~1', '~', '~', '~4', '~', 0.1, 25),
                chat('Se acerca la tormenta')
            ]))
        ]
    },
    {
        id: 'medium-mini-arena',
        level: 'medium',
        title: 'Mini arena',
        description: 'Prepara una pista de piedra delante con columnas de cristal.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSetBlocks('Stone', '~-4', '~-1', '~-4', '~4', '~-1', '~4'),
                frontSetBlocks('Glass', '~-4', '~', '~-4', '~-4', '~3', '~-4'),
                frontSetBlocks('Glass', '~4', '~', '~-4', '~4', '~3', '~-4'),
                frontSetBlocks('Glass', '~-4', '~', '~4', '~-4', '~3', '~4'),
                frontSetBlocks('Glass', '~4', '~', '~4', '~4', '~3', '~4'),
                teleport('~', '~1', '~2')
            ]))
        ]
    },
    {
        id: 'medium-particle-trail',
        level: 'medium',
        title: 'Rastro de particulas',
        description: 'Dibuja una estela de fuego hacia delante.',
        variables: [localVariable('paso')],
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                setVariable('paso', 0),
                repeat(7, [
                    spawnParticle(
                        'Flame',
                        '~',
                        '~1',
                        frontVariableZ('paso'),
                        '~',
                        '~2',
                        frontVariableZ('paso'),
                        numberInput(0.1),
                        numberInput(8)
                    ),
                    changeVariable('paso', 1)
                ])
            ]))
        ]
    },
    {
        id: 'medium-build-and-reset',
        level: 'medium',
        title: 'Construye y reinicia',
        description: 'Levanta un bloque de diamante delante, espera y limpia la zona.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSetBlocks('Diamond Block', '~-2', '~', '~-2', '~2', '~2', '~2'),
                wait(2),
                resetAroundHere(6)
            ]))
        ]
    },
    {
        id: 'medium-chat-coordinates',
        level: 'medium',
        title: 'Mensaje con coordenadas',
        description: 'Escribe tu posicion actual en el chat.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                chat(joinAll('Estoy en ', getPosX(), ', ', getPosY(), ', ', getPosZ()))
            ]))
        ]
    }
];

const advancedExamples = [
    {
        id: 'advanced-double-stairs',
        level: 'advanced',
        title: 'Escalera doble',
        description: 'Dos escaleras simetricas que avanzan hacia delante.',
        variables: [localVariable('paso')],
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                setVariable('paso', 0),
                repeat(8, [
                    setBlock('Cobblestone', '~-1', relativeFromVariable('paso'), frontVariableZ('paso')),
                    setBlock('Cobblestone', '~1', relativeFromVariable('paso'), frontVariableZ('paso')),
                    changeVariable('paso', 1)
                ])
            ]))
        ]
    },
    {
        id: 'advanced-lit-tunnel',
        level: 'advanced',
        title: 'Tunel iluminado',
        description: 'Excava un tunel hacia delante con luces en el techo.',
        variables: [localVariable('paso')],
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                setVariable('paso', 0),
                repeat(8, [
                    setBlock('Stone', '~', relativeFromVariable('paso'), frontVariableZ('paso')),
                    setBlock('Glowstone', '~', join('~', add(variableReporter('paso'), numberInput(2))), frontVariableZ('paso')),
                    changeVariable('paso', 1)
                ])
            ]))
        ]
    },
    {
        id: 'advanced-villager-line',
        level: 'advanced',
        title: 'Fila de aldeanos',
        description: 'Genera aldeanos en fila delante del jugador.',
        variables: [localVariable('paso')],
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                setVariable('paso', 0),
                repeat(5, [
                    spawnEntity('Villager', '~', '~', frontVariableZ('paso')),
                    changeVariable('paso', 2)
                ])
            ]))
        ]
    },
    {
        id: 'advanced-layered-beacon',
        level: 'advanced',
        title: 'Faro por capas',
        description: 'Apila capas de cristal delante y remata con una luz arriba.',
        variables: [localVariable('altura')],
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                setVariable('altura', 0),
                repeat(6, [
                    frontSetBlocks(
                        'Glass',
                        '~-1',
                        relativeFromVariable('altura'),
                        '~-1',
                        '~1',
                        relativeFromVariable('altura'),
                        '~1'
                    ),
                    changeVariable('altura', 1)
                ]),
                frontSetBlocks('Glowstone', '~', '~6', '~', '~', '~8', '~')
            ]))
        ]
    },
    {
        id: 'advanced-portal-particles',
        level: 'advanced',
        title: 'Portal de particulas',
        description: 'Dibuja un portal vertical delante con particulas.',
        variables: [localVariable('altura')],
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                setVariable('altura', 0),
                repeat(5, [
                    frontSpawnParticle(
                        'Portal',
                        '~',
                        relativeFromVariable('altura'),
                        '~',
                        '~',
                        relativeFromVariable('altura'),
                        '~',
                        0.2,
                        10
                    ),
                    changeVariable('altura', 1)
                ])
            ]))
        ]
    },
    {
        id: 'advanced-safe-elevator',
        level: 'advanced',
        title: 'Ascensor seguro',
        description: 'Levanta una columna de cristal delante y sube al jugador.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                changeGameMode(1),
                frontSetBlocks('Glass', '~', '~', '~', '~', '~8', '~'),
                teleport('~', '~8', '~4'),
                chat('Ascensor listo')
            ]))
        ]
    },
    {
        id: 'advanced-inspect-repair',
        level: 'advanced',
        title: 'Inspeccion y reparacion',
        description: 'Revisa el suelo delante y coloca piedra si hace falta.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSearchBlock('~', '~-1', '~'),
                ifElse(
                    checkBlockType('Stone'),
                    [chat('Suelo firme delante')],
                    [
                        frontSetBlock('Stone', '~', '~-1', '~'),
                        chat('Repare el suelo')
                    ]
                )
            ]))
        ]
    },
    {
        id: 'advanced-enchanted-plaza',
        level: 'advanced',
        title: 'Plaza encantada',
        description: 'Prepara una plaza de piedra delante con particulas y encantamiento.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                frontSetBlocks('Stone', '~-3', '~-1', '~-3', '~3', '~-1', '~3'),
                changeWeather(0),
                giveEnchant(2, 0),
                frontSpawnParticle('Happy Villager', '~', '~1', '~', '~', '~3', '~', 0.1, 20)
            ]))
        ]
    },
    {
        id: 'advanced-mob-festival',
        level: 'advanced',
        title: 'Festival de mobs',
        description: 'Varios animales y particulas delante del jugador.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                changeWeather(1),
                frontSpawnEntity('Cow', '~-2', '~', '~'),
                frontSpawnEntity('Sheep', '~2', '~', '~'),
                frontSpawnEntity('Pig', '~-2', '~', '~2'),
                frontSpawnEntity('Villager', '~2', '~', '~2'),
                frontSpawnParticle('Heart', '~', '~2', '~', '~', '~4', '~', 0.1, 18)
            ]))
        ]
    },
    {
        id: 'advanced-full-show',
        level: 'advanced',
        title: 'Show completo',
        description: 'Combina tortuga, particulas y animales delante del jugador.',
        scripts: [
            greenFlagScript(56, 64, connectAndBuild([
                chat('Empieza el show'),
                changeWeather(0)
            ])),
            greenFlagScript(360, 64, connectAndBuild([
                repeat(4, [
                    turtleMove('forward', 4),
                    turtleTurn('yaw', 90)
                ])
            ])),
            greenFlagScript(56, 300, connectAndBuild([
                frontSpawnParticle('Flame', '~', '~2', '~', '~', '~5', '~', 0.2, 24),
                frontSpawnEntity('Cow', '~2', '~', '~')
            ]))
        ]
    }
];

export const EXAMPLE_LEVELS = [
    {
        id: 'basic',
        label: 'Basico'
    },
    {
        id: 'medium',
        label: 'Nivel medio'
    },
    {
        id: 'advanced',
        label: 'Avanzado'
    }
];

export const EXAMPLES = [
    ...basicExamples,
    ...mediumExamples,
    ...advancedExamples
];
